import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import QuratorLayout from '../components/qurator/QuratorLayout';
import API from '../config/api';

const PublishedCopilots = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [protocols, setProtocols] = useState([]);
  const [orgUsers, setOrgUsers] = useState([]);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [sort, setSort] = useState(searchParams.get('sort') || 'date');
  const [order, setOrder] = useState(searchParams.get('order') || 'DESC');
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedProtocol, setSelectedProtocol] = useState(null);
  const [shareForm, setShareForm] = useState({ share_to: '', message: '' });

  useEffect(() => {
    fetchProtocols();
    fetchOrgUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, sort, order]);

  const fetchProtocols = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        ...(search && { search }),
        sort,
        order
      });
      
      const response = await API.get(`/qurator/protocols/published?${params}`);
      if (response.data.success) {
        setProtocols(response.data.protocols || []);
        console.log('Published copilots loaded:', response.data.protocols?.length || 0);
      } else {
        console.error('Failed to fetch published copilots:', response.data.message);
        setProtocols([]);
      }
    } catch (error) {
      console.error('Error fetching published copilots:', error);
      console.error('Error details:', error.response?.data);
      setProtocols([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrgUsers = async () => {
    try {
      const response = await API.get('/qurator/users/organization');
      if (response.data.success) {
        setOrgUsers(response.data.users);
      }
    } catch (error) {
      console.error('Error fetching organization users:', error);
    }
  };

  const handleSearch = (value) => {
    setSearch(value);
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set('search', value);
    } else {
      params.delete('search');
    }
    navigate({ search: params.toString() });
  };

  const handleSort = (column) => {
    // Map frontend column names to match PHP logic
    const columnMap = {
      'title': 'title',
      'description': 'description',
      'date': 'date'
    };
    const mappedColumn = columnMap[column] || 'date';
    
    const newOrder = sort === mappedColumn && order === 'DESC' ? 'ASC' : 'DESC';
    setSort(mappedColumn);
    setOrder(newOrder);
    
    const params = new URLSearchParams(searchParams);
    params.set('sort', mappedColumn);
    params.set('order', newOrder);
    navigate({ search: params.toString() });
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this protocol? This action cannot be undone.')) {
      try {
        const response = await API.delete(`/qurator/protocols/${id}`);
        if (response.data.success) {
          fetchProtocols();
        }
      } catch (error) {
        console.error('Error deleting protocol:', error);
        alert('Failed to delete protocol');
      }
    }
  };

  const openShareModal = (protocol) => {
    setSelectedProtocol(protocol);
    setShareForm({ share_to: '', message: '' });
    setShowShareModal(true);
  };

  const handleShare = async (e) => {
    e.preventDefault();
    try {
      const response = await API.post('/qurator/protocols/share', {
        protocol_id: selectedProtocol.prt_id,
        share_to: shareForm.share_to,
        message: shareForm.message
      });

      if (response.data.success) {
        alert('Protocol shared successfully!');
        setShowShareModal(false);
        setSelectedProtocol(null);
        setShareForm({ share_to: '', message: '' });
      }
    } catch (error) {
      console.error('Error sharing protocol:', error);
      alert('Failed to share protocol');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getSortIcon = (column) => {
    // Map display column to sort column
    const columnMap = {
      'title': 'title',
      'description': 'description',
      'date': 'date'
    };
    const mappedColumn = columnMap[column] || 'date';
    
    if (sort !== mappedColumn) return '↕';
    return order === 'ASC' ? '↑' : '↓';
  };

  if (loading) {
    return (
      <QuratorLayout>
        <div className="simulations-loading">
          <div className="qrt-loading"></div>
          <p>Loading published copilots...</p>
        </div>
      </QuratorLayout>
    );
  }

  return (
    <QuratorLayout>
      <div className="container-fluid px-4 py-4">
        <div className="page-header">
          <div className="d-flex justify-content-between align-items-center flex-wrap">
            <div>
              <h4 className="mb-1">Published Copilots</h4>
              <p className="mb-0">Manage your published copilots</p>
            </div>
            <Link to="/drafts?project_id=0&type=mr" className="btn btn-light">
              <i className="bi bi-plus-lg me-2"></i>New Simulation
            </Link>
          </div>
        </div>

        <div className="main-card">
          <div className="p-3 border-bottom">
            <div className="search-box">
              <i className="bi bi-search"></i>
              <input
                type="text"
                id="searchInput"
                placeholder="Search simulations..."
                value={search}
                onChange={(e) => handleSearch(e.target.value)}
              />
            </div>
          </div>

          <div className="table-responsive">
            <table id="protocolTable" className="table">
              <thead>
                <tr>
                  <th 
                    className={`sort-header ${sort === 'title' ? order.toLowerCase() : ''}`}
                    onClick={() => handleSort('title')}
                    style={{ cursor: 'pointer' }}
                  >
                    Title {getSortIcon('title')}
                  </th>
                  <th className="description-cell">Description</th>
                  <th 
                    className={`sort-header date-column ${sort === 'date' ? order.toLowerCase() : ''}`}
                    onClick={() => handleSort('date')}
                    style={{ cursor: 'pointer' }}
                  >
                    Date {getSortIcon('date')}
                  </th>
                  <th width="200" className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {protocols.length > 0 ? (
                  protocols.map((protocol) => (
                    <tr key={protocol.prt_id}>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <span>{protocol.draft_title}</span>
                          {protocol.favorite === '1' && (
                            <i className="bi bi-star-fill text-warning"></i>
                          )}
                        </div>
                      </td>
                      <td className="description-cell">
                        {protocol.prt_description 
                          ? (protocol.prt_description.length > 100 
                              ? protocol.prt_description.substring(0, 100) + '...'
                              : protocol.prt_description)
                          : '-'}
                      </td>
                      <td className="date-column">
                        {formatDate(protocol.date)}
                      </td>
                      <td>
                        <div className="action-buttons">
                          <Link
                            to={`/play-simulation?id=${protocol.prt_id}`}
                            className="action-btn play-btn"
                            title="Play"
                          >
                            <i className="bi bi-play-fill"></i>
                          </Link>
                          <Link
                            to={`/draft-read?viewid=${protocol.prt_id}`}
                            className="action-btn view-btn"
                            title="View"
                          >
                            <i className="bi bi-eye"></i>
                          </Link>
                          <Link
                            to={`/draft-edit?editid=${protocol.prt_id}`}
                            className="action-btn edit-btn"
                            title="Edit"
                          >
                            <i className="bi bi-pencil"></i>
                          </Link>
                          <button
                            type="button"
                            className="action-btn share-btn"
                            title="Share"
                            onClick={() => openShareModal(protocol)}
                          >
                            <i className="bi bi-share"></i>
                          </button>
                          <button
                            type="button"
                            className="action-btn delete-btn"
                            title="Delete"
                            onClick={() => handleDelete(protocol.prt_id)}
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="text-center py-5">
                      <div className="empty-state">
                        <i className="bi bi-inbox empty-state-icon"></i>
                        <div className="empty-state-title">No published copilots found</div>
                        <div className="empty-state-text">
                          {search ? 'Try adjusting your search' : 'Publish your first copilot to get started'}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Share Modal */}
      {showShareModal && selectedProtocol && (
        <div className="modal-overlay" onClick={() => setShowShareModal(false)}>
          <div className="modal-custom" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h5 className="modal-title">Share Protocol</h5>
              <button 
                type="button" 
                className="btn-close btn-close-white" 
                onClick={() => setShowShareModal(false)}
              ></button>
            </div>
            <form onSubmit={handleShare}>
              <div className="modal-body">
                <p className="mb-3 text-muted">
                  Protocol: {selectedProtocol.draft_title}
                </p>
                
                <div className="mb-3">
                  <label className="form-label">Share with User</label>
                  <select
                    className="form-select"
                    value={shareForm.share_to}
                    onChange={(e) => setShareForm({ ...shareForm, share_to: e.target.value })}
                    required
                  >
                    <option value="">Select a user...</option>
                    {orgUsers.map((user) => (
                      <option key={user.user_id} value={user.user_id}>
                        {user.username} 
                        {user.first_name || user.last_name 
                          ? ` (${user.first_name || ''} ${user.last_name || ''})`.trim()
                          : ''}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="mb-3">
                  <label className="form-label">Message (Optional)</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    placeholder="Add a note about this protocol..."
                    value={shareForm.message}
                    onChange={(e) => setShareForm({ ...shareForm, message: e.target.value })}
                  ></textarea>
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowShareModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">Share</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </QuratorLayout>
  );
};

export default PublishedCopilots;
